import { InfoIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import {
  ActionBarActions,
  ActionBarRoot,
  ActionBarSaveButton,
  ActionBarStatus,
} from '@crossnokaye/ux-common/ckds';

import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { Button } from '@/ux-common/components/button/button';
import { withTooltip } from '@/ux-common/components/tooltip/with-tooltip';

import { DISCARD_ACTION_LABEL } from '../reset-faulted.constants';

const SaveBarSaveButtonWithTooltip = withTooltip(ActionBarSaveButton);

interface ResetFaultedSaveBarProps {
  onCancel: () => void;
  onReset: () => void;
  selectedCount: number;
}

/**
 * Bottom save bar of the Reset Faulted pane. Opens whenever any device is
 * selected; Discard clears the selection and Review & Save opens the confirm dialog.
 * Button copy matches the bulk edit save bar.
 */
export const ResetFaultedSaveBar = ({
  onCancel,
  onReset,
  selectedCount,
}: ResetFaultedSaveBarProps): ReactNode => {
  const { isDisabled: disabledByPermissions, tooltipText: permissionTooltipText } =
    useActionPermission({ privilege: PRIVILEGES.writeCommands });

  return (
    <ActionBarRoot
      className="relative"
      data-component="ResetFaultedSaveBar"
      data-testid="reset-faulted-save-bar"
      open={selectedCount > 0}
    >
      <ActionBarStatus
        className="uppercase text-ckds-base-caution"
        icon={InfoIcon}
        statusText={`${selectedCount} device(s) selected`}
        variant="default"
      />
      <ActionBarActions>
        <Button
          className="btn btn-tier2"
          data-testid="reset-faulted-cancel-button"
          onClick={onCancel}
          type="button"
        >
          {DISCARD_ACTION_LABEL}
        </Button>
        <SaveBarSaveButtonWithTooltip
          disabled={disabledByPermissions}
          onClick={onReset}
          tooltipArrow
          tooltipContent={permissionTooltipText}
          type="button"
        >
          {`REVIEW & SAVE ${selectedCount} CHANGES`}
        </SaveBarSaveButtonWithTooltip>
      </ActionBarActions>
    </ActionBarRoot>
  );
};
